import React from 'react';
import {ScrollView, Text} from 'react-native';
import {storiesOf} from '@storybook/react-native';

import {
  Code,
  FlexView,
  Heading,
  Spacer,
  Spacing,
} from '/component/base/Storybook';
import {LABEL_COMING_SOON} from '/enum/label.enum';

import FormattedNumber from '../FormattedNumber';

storiesOf('base', module).add('FormattedNumber', () => (
  <ScrollView>
    <Spacing size={20}>
      <FlexView>
        <Heading level={1}>FormattedNumber</Heading>
        <Spacer size={16} />
        <Text>
          The <Code>FormattedNumber</Code> component is used to render number
          values that need proper formatting. This includes decimal separators,
          thousand separators, currency symbols, and currency codes.
        </Text>
        <Spacer size={16} />
        <Text>
          The value of the amount is passed into the{' '}
          <Code>FormattedNumber</Code> component via the <Code>value</Code>{' '}
          props. Positive and negative values are handled.
        </Text>
        <Spacer size={16} />
        <FlexView direction="horizontal">
          <FlexView align="center">
            <FormattedNumber value={99} />
            <Spacer size={6} />
            <Code>{'value={99}'}</Code>
          </FlexView>
          <Spacer direction="horizontal" size={16} />
          <FlexView align="center">
            <FormattedNumber value={-99} />
            <Spacer size={6} />
            <Code>{'value={-99}'}</Code>
          </FlexView>
        </FlexView>
        <Spacer size={24} />
        <Heading level={2}>Currency</Heading>
        <Spacer size={16} />
        <Text>
          To display currency, pass a valid currency code to the{' '}
          <Code>currencyCode</Code> prop. The component will take care of
          converting the currency code to the associated currency symbol.
        </Text>
        <Spacer size={16} />
        <FlexView direction="horizontal">
          <FlexView align="center">
            <FormattedNumber value={99} currencyCode="USD" />
            <Spacer size={6} />
            <Code>{'currencyCode="USD"'}</Code>
          </FlexView>
          <Spacer direction="horizontal" size={16} />
          <FlexView align="center">
            <FormattedNumber value={-99} currencyCode="EUR" />
            <Spacer size={6} />
            <Code>{'currencyCode="EUR"'}</Code>
          </FlexView>
        </FlexView>
        <Spacer size={16} />
        <Text>
          To display the currency code as well, pass{' '}
          <Code>{'displayCurrencyCode={true}'}</Code> prop. The currency code
          will be rendered as a suffix.
        </Text>
        <Spacer size={16} />
        <FlexView>
          <FormattedNumber
            // @ts-expect-error TS(2322): Type '{ value: number; currencyCode: string; displ... Remove this comment to see the full error message
            value={99}
            currencyCode="USD"
            displayCurrencyCode={true}
          />
          <Spacer size={6} />
          <Code>{'currencyCode="USD" displayCurrencyCode={true}'}</Code>
        </FlexView>
        <Spacer size={24} />
        <Heading level={2}>Number of Decimal Places</Heading>
        <Spacer size={16} />
        <Text>
          By default 0 decimal places are rendered. The number of decimal places
          can be specified via the <Code>decimalPlaces</Code> props.
        </Text>
        <Spacer size={16} />
        <FlexView direction="horizontal">
          <FlexView align="center">
            <FormattedNumber value={99} decimalPlaces={1} />
            <Spacer size={6} />
            <Code>{'decimalPlaces={1}'}</Code>
          </FlexView>
          <Spacer direction="horizontal" size={16} />
          <FlexView align="center">
            <FormattedNumber value={99} decimalPlaces={2} />
            <Spacer size={6} />
            <Code>{'decimalPlaces={2}'}</Code>
          </FlexView>
        </FlexView>
        <Spacer size={24} />
        <Heading level={2}>Thousand Separator and Decimal Symbol</Heading>
        <Spacer size={16} />
        <Text>
          By default large values are rendered with <Code>,</Code> as separator,
          every 3 digits.
        </Text>
        <Spacer size={16} />
        <Text>
          The separation style can be specified via the{' '}
          <Code>separationStyle</Code> prop. Valid values are{' '}
          <Code>thousand</Code> (default style), <Code>lakh</Code> (Indian
          style) and <Code>wan</Code> (CJK style).
        </Text>
        <Spacer size={16} />
        <FlexView>
          <FormattedNumber
            // @ts-expect-error TS(2322): Type '{ value: number; currencyCode: string; separ... Remove this comment to see the full error message
            value={1234567890}
            currencyCode="USD"
            separationStyle="thousand"
          />
          <Spacer size={6} />
          <Code>{'currencyCode="USD" separationStyle="thousand"'}</Code>
        </FlexView>
        <Spacer size={16} />
        <FlexView>
          <FormattedNumber
            // @ts-expect-error TS(2322): Type '{ value: number; currencyCode: string; separ... Remove this comment to see the full error message
            value={1234567890}
            currencyCode="INR"
            separationStyle="lakh"
          />
          <Spacer size={6} />
          <Code>{'currencyCode="INR" separationStyle="lakh"'}</Code>
        </FlexView>
        <Spacer size={16} />
        <FlexView>
          <FormattedNumber
            // @ts-expect-error TS(2322): Type '{ value: number; currencyCode: string; separ... Remove this comment to see the full error message
            value={1234567890}
            currencyCode="JPN"
            separationStyle="wan"
          />
          <Spacer size={6} />
          <Code>{'currencyCode="JPN" separationStyle="wan"'}</Code>
        </FlexView>
        <Spacer size={16} />
        <Text>
          The separation symbol can be specified via the <Code>separator</Code>{' '}
          prop, and the decimal symbol the <Code>decimalSymbol</Code> prop.
          These 2 props can not be the same.
        </Text>
        <Spacer size={16} />
        <FlexView>
          <FormattedNumber
            // @ts-expect-error TS(2322): Type '{ value: number; currencyCode: string; separ... Remove this comment to see the full error message
            value={1234567890}
            currencyCode="DKK"
            separator="."
            decimalSymbol=","
          />
          <Spacer size={6} />
          <Code>{'currencyCode="DKK" separator="." decimalSymbol=","'}</Code>
        </FlexView>
        <Spacer size={16} />
        <Heading level={2}>Coming Soon Label</Heading>
        <Spacer size={16} />
        <FlexView>
          <FormattedNumber value={LABEL_COMING_SOON} />
          <Spacer size={6} />
          <Code>{'value="LABEL_COMING_SOON"'}</Code>
        </FlexView>
        <Spacer size={16} />
        <Heading level={2}>Label Styles</Heading>
        <Spacer size={16} />
        <Text>
          It is also possible to change the weight of the text. Text bold by
          default.
        </Text>
        <Spacer size={16} />
        <FlexView>
          <FormattedNumber
            // @ts-expect-error TS(2322): Type '{ value: number; boldText: boolean; style: {... Remove this comment to see the full error message
            value={1234567890}
            boldText={false}
            style={{text: null}}
          />
          <Spacer size={6} />
          <Code>{'boldText={false}'}</Code>
        </FlexView>
        <Spacer size={16} />
        <Text>Аnd send other styles depending on conditions.</Text>
        <Spacer size={16} />
        <FlexView>
          <FormattedNumber
            // @ts-expect-error TS(2322): Type '{ value: number; style: { text: { color: str... Remove this comment to see the full error message
            value={1234567890}
            style={{text: {color: '#FF0000', borderWidth: 1}}}
          />
          <Spacer size={6} />
          <Code>{'style={{text: {color: "#FF0000"}}}'}</Code>
        </FlexView>
      </FlexView>
    </Spacing>
  </ScrollView>
));
